import { Base, CardTitle } from "@/atoms-react/text/Text.react";
import { AnimatedShield } from "@/components/animations/shield/index.react";
import { KYCStatusType } from "@/features/kyc/types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export type OlkypassCardProps = {
  CTA?: boolean;
  currentStatus: KYCStatusType | null;
  verifyButton?: boolean;
  small?: boolean;
  nextStatus?: KYCStatusType | null;
  dashboard?: boolean;
  auditPending: boolean;
  withoutButton?: boolean;
  className?: string;
};

import { ModalOlkyPass } from "@/molecules/modal-olkypass/index.react";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { useState } from "react";

import { Button } from "@/atoms-react/button/Button.react";
import { Shield } from "@/atoms-react/shield/index.react";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { ModalKYCIdentity } from "@/molecules/modal-kyc-identity/index.react";
import { Themes } from "@/types/theme";
import { OlkyPassRoutes } from "@/utils/routes";

export const OlkypassCard = ({
  currentStatus,
  nextStatus,
  CTA = true,
  dashboard = false,
  auditPending,
  withoutButton,
  className = ""
}: OlkypassCardProps) => {
  const [modalIdentity, setModalIdentity] = useState<boolean>(false);
  const origin = useOrigin();
  const { t } = useTranslation(["pass"]);
  const navigate = useNavigate();
  const { leUuid } = useCompanyContext();
  // Use States et variables for the modal
  const modalOlkyPassAllSteps = [
    {
      id: 0,
      title: t("olkypass.mainPage.olkypass.modalOlkyPass.status_declarative"),
      description: t("olkypass.mainPage.olkypass.levelPage.declarative_description"),
      imageSource: "/images/olkypass/status_declarative.png",
      imageSourceInter: "/images/olkypass/status_declarative_inter.png",
      imageSourceEn: "/images/olkypass/status_declarativeEn.png"
    },
    {
      id: 1,
      title: t("olkypass.mainPage.olkypass.modalOlkyPass.status_verified"),
      description: t("olkypass.mainPage.olkypass.levelPage.verified_description"),
      imageSource: "/images/olkypass/status_verified.png",
      imageSourceInter: "/images/olkypass/status_verified_inter.png",
      imageSourceEn: "/images/olkypass/status_verifiedEn.png",
      imageSourceInterEn: "/images/olkypass/status_verified_interEn.png"
    },
    {
      id: 2,
      title: t("olkypass.mainPage.olkypass.modalOlkyPass.status_authenticated"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_description"),
      imageSource: "/images/olkypass/status_authenticated.png",
      imageSourceInter: "/images/olkypass/status_authenticated_inter.png",
      imageSourceEn: "/images/olkypass/status_authenticatedEn.png",
      imageSourceInterEn: "/images/olkypass/status_authenticated_interEn.png"
    },

    {
      id: 3,
      title: t("olkypass.mainPage.olkypass.modalOlkyPass.status_authenticated_bronz"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_bronz_description"),
      imageSource: "/images/olkypass/status_authenticated_bronze.png",
      imageSourceInter: "/images/olkypass/status_authenticated_bronze_inter.png",
      imageSourceEn: "/images/olkypass/status_authenticated_bronzeEn.png",
      imageSourceInterEn: "/images/olkypass/status_authenticated_bronze_interEn.png"
    },
    {
      id: 4,
      title: t("olkypass.mainPage.olkypass.modalOlkyPass.status_authenticated_silver"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_silver_description"),
      imageSource: "/images/olkypass/status_authenticated_silver.png",
      imageSourceInter: "/images/olkypass/status_authenticated_silver_inter.png",
      imageSourceEn: "/images/olkypass/status_authenticated_silverEn.png",
      imageSourceInterEn: "/images/olkypass/status_authenticated_silver_interEn.png"
    },
    {
      id: 5,
      title: t("olkypass.mainPage.olkypass.modalOlkyPass.status_authenticated_gold"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_gold_description"),
      imageSource: "/images/olkypass/status_authenticated_Gold.png",
      imageSourceInter: "/images/olkypass/status_authenticated_gold_inter.png",
      imageSourceEn: "/images/olkypass/status_authenticated_GoldEn.png",
      imageSourceInterEn: "/images/olkypass/status_authenticated_gold_interEn.png"
    }
  ];
  const [isOlkyPassModalOpen, setIsOlkyPassModalOpen] = useState<boolean>(false);
  const { locale } = useLocale();

  const filteredSteps = (() => {
    const st = auditPending ? nextStatus : currentStatus;

    switch (st) {
      case KYCStatusType.DECLARATIVE:
        return modalOlkyPassAllSteps[0];
      case KYCStatusType.VERIFIED:
        return modalOlkyPassAllSteps[1];
      case KYCStatusType.AUTHENTICATED:
        return modalOlkyPassAllSteps[2];
      case KYCStatusType.AUTHENTICATED_BRONZE:
        return modalOlkyPassAllSteps[3];
      case KYCStatusType.AUTHENTICATED_SILVER:
        return modalOlkyPassAllSteps[4];
      case KYCStatusType.AUTHENTICATED_GOLD:
        return modalOlkyPassAllSteps[5];
      default:
        break;
    }
  })();

  const status = auditPending ? nextStatus : currentStatus;

  const handleModalAction = () => {
    setIsOlkyPassModalOpen(false);
    switch (status) {
      case KYCStatusType.DECLARATIVE:
        setModalIdentity(true);
        break;
      case KYCStatusType.VERIFIED:
        navigate("/pass/support");
        break;
      case KYCStatusType.AUTHENTICATED:
        navigate("/pass/support");
        break;
      case KYCStatusType.AUTHENTICATED_BRONZE:
        navigate("/pass/support");
        break;
      case KYCStatusType.AUTHENTICATED_SILVER:
        navigate("/pass/support");
        break;
      case KYCStatusType.AUTHENTICATED_GOLD:
        navigate("/pass/support");
        break;
      default:
        break;
    }
  };

  return (
    <>
      <ModalOlkyPass
        isOpen={isOlkyPassModalOpen}
        intermediate={auditPending}
        imageUrl={
          (auditPending
            ? locale.language === "FR"
              ? filteredSteps?.imageSourceInter
              : filteredSteps?.imageSourceInterEn
            : locale.language === "FR"
              ? filteredSteps?.imageSource
              : filteredSteps?.imageSourceEn) as string
        }
        title={filteredSteps?.title ?? ""}
        description={
          auditPending
            ? t("olkypass.mainPage.olkypass.modalOlkyPass.kyc_status_intermediate")
            : (filteredSteps?.description ?? "")
        }
        onClose={() => setIsOlkyPassModalOpen(false)}
        onClick={handleModalAction}
      />
      <div
        className={twMerge(
          `h-[188px] w-full flex-1 rounded-sm bg-cover bg-left-bottom text-left lg:h-[188px] 2xl:h-[223px] ${
            dashboard ? "xl:w-4/12" : ""
          }  `,
          status === KYCStatusType.DECLARATIVE &&
            "bg-[url('/images/olkypass/declarative_kyc_bg.png')]",
          status === KYCStatusType.AUTHENTICATED_BRONZE &&
            "bg-[url('/images/olkypass/declarative_authenticted_bronze_kyc_bg.png')]",
          status === KYCStatusType.VERIFIED && "bg-[url('/images/olkypass/verified_kyc_bg.png')]",
          status === KYCStatusType.AUTHENTICATED &&
            "bg-[url('/images/olkypass/authenticated_kyc_bg.png')]",
          status === KYCStatusType.AUTHENTICATED_SILVER &&
            "bg-[url('/images/olkypass/declarative_authenticted_silver_kyc_bg.png')]",
          status === KYCStatusType.AUTHENTICATED_GOLD &&
            "bg-[url('/images/olkypass/declarative_authenticted_or_kyc_bg.png')]",

          //status === KYCStatusType.LOADING && 'bg-gradient-to-b from-gray-500 to-gray-500/50',
          "min-h-40",
          className
        )}
      >
        <div
          className={twMerge(
            "h-full w-full flex-1 rounded-sm",
            CTA && "hover:from-lime-500 hover:to-lime-500/50",
            "min-h-48"
          )}
        >
          <div className="relative flex items-center justify-between gap-x-2 px-4 py-3 sm:gap-x-8 lg:gap-x-0 lg:px-5 lg:py-5 2xl:px-6 2xl:py-5">
            <div>
              <div
                className=":mt-0 mb-1 flex cursor-pointer items-center justify-start gap-x-2 text-white hover:text-white/80"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOlkyPassModalOpen(true);
                }}
              >
                <InformationCircleIcon className="h-6 w-6 text-inherit" />
                <CardTitle
                  className={"absolute left-14 flex font-semibold text-sm text-white lg:left-13"}
                >
                  {t("olkypass.level.status")}
                  <div
                    data-cy={`olkypass-status-${status}`}
                    className={twMerge(
                      `font-semibold text-black text-black text-sm ${
                        auditPending ? "bg-orange-500 text-white" : "bg-green-500"
                      } ml-2 rounded-sm px-2`
                    )}
                  >
                    {status === KYCStatusType.AUTHENTICATED &&
                      t("olkypass.level.authenticated_title")}
                    {status === KYCStatusType.AUTHENTICATED_BRONZE &&
                      t("olkypass.level.authenticated_bronze_title")}
                    {status === KYCStatusType.AUTHENTICATED_SILVER &&
                      t("olkypass.level.authenticated_silver_title")}
                    {status === KYCStatusType.AUTHENTICATED_GOLD &&
                      t("olkypass.level.authenticated_gold_title")}
                    {status === KYCStatusType.VERIFIED && t("olkypass.level.verified_title")}
                    {status === KYCStatusType.DECLARATIVE && t("olkypass.level.declarative_title")}
                  </div>
                </CardTitle>
              </div>

              <div className={twMerge("min-h-28")}>
                <div className="mb-16 inline-grid grid-cols-6 sm:mb-14 lg:mb-14 xl:mb-11 2xl:mb-20">
                  <Shield
                    intermediate={auditPending}
                    verified={status === KYCStatusType.DECLARATIVE}
                  />
                  <Shield
                    intermediate={auditPending}
                    verified={status === KYCStatusType.VERIFIED}
                  />
                  <Shield
                    intermediate={auditPending}
                    verified={status === KYCStatusType.AUTHENTICATED}
                  />
                  <Shield
                    intermediate={auditPending}
                    verified={status === KYCStatusType.AUTHENTICATED_BRONZE}
                  />
                  <Shield
                    intermediate={auditPending}
                    verified={status === KYCStatusType.AUTHENTICATED_SILVER}
                  />
                  <Shield
                    intermediate={auditPending}
                    verified={status === KYCStatusType.AUTHENTICATED_GOLD}
                  />
                </div>
                {auditPending && (
                  <Base className={`absolute top-20 top-20 left-6 text-white lg:mt-3 xl:left-7`}>
                    {t("olkypass.mainPage.olkypass.modalOlkyPass.verification")}
                  </Base>
                )}{" "}
                {!withoutButton &&
                  (origin === FromURL.Partner ? (
                    <div className="mt-3 flex justify-start">
                      <Base
                        onClick={() => {
                          navigate(
                            leUuid ? `${OlkyPassRoutes.level}/${leUuid}` : OlkyPassRoutes.level
                          );
                        }}
                        className="cursor-pointer text-base text-white"
                      >
                        <span className="z-10 flex items-center justify-center space-x-2 overflow-hidden text-ellipsis whitespace-nowrap">
                          <span className="w-max truncate">
                            {t("olkypass.mainPage.button.myPassport")}
                          </span>
                        </span>
                      </Base>
                    </div>
                  ) : (
                    <div className="flex justify-start">
                      <Button
                        onClick={() =>
                          navigate(
                            leUuid ? `${OlkyPassRoutes.level}/${leUuid}` : OlkyPassRoutes.level
                          )
                        }
                        size="md"
                        disabled={auditPending}
                        theme={Themes.declarative}
                        className="h-8 w-48 items-center justify-center border-none bg-white text-black text-xs sm:h-10 sm:w-64 sm:text-sm md:w-68 lg:h-8 lg:w-64 lg:text-sm xl:h-10 xl:w-64 xl:text-base xl:text-base"
                      >
                        <span className="z-10 flex items-center justify-center space-x-2 overflow-hidden text-ellipsis whitespace-nowrap">
                          <span className="w-max truncate">
                            {status === KYCStatusType.AUTHENTICATED_GOLD
                              ? t("olkypass.mainPage.button.show_olkypass")
                              : t("olkypass.mainPage.button.declarativeButton")}
                          </span>
                        </span>
                      </Button>
                    </div>
                  ))}
              </div>
            </div>

            <div>
              {status === KYCStatusType.AUTHENTICATED_GOLD && (
                <AnimatedShield
                  color={"#D09A31"}
                  style="top-14 left-3 2xl:left-4 w-21 h-8"
                  percentage={100}
                />
              )}
              {status === KYCStatusType.AUTHENTICATED_SILVER && (
                <AnimatedShield
                  color={"#B7B7B7"}
                  style="top-14 left-4 2xl:left-4 w-21 h-8"
                  percentage={78}
                />
              )}
              {status === KYCStatusType.AUTHENTICATED_BRONZE && (
                <AnimatedShield
                  color={"#652E10"}
                  style="top-14  left-4  2xl:left-4  w-21 w-18 h-8"
                  percentage={70}
                />
              )}

              {status === KYCStatusType.AUTHENTICATED && (
                <AnimatedShield
                  style="bottom-11  left-4 w-20 2xl:left-4 2xl:w-21 h-8"
                  color={"#82C805"}
                  percentage={60}
                />
              )}
              {status === KYCStatusType.VERIFIED && (
                <AnimatedShield
                  style=" bottom-10 left-6 2xl:left-7  w-14 2xl:w-18 h-6"
                  color={"#1D98DF"}
                  percentage={50}
                />
              )}
              {status === KYCStatusType.DECLARATIVE && (
                <AnimatedShield
                  style=" bottom-9 left-6 2xl:left-8 w-15 h-5 "
                  color={"#D11E1F"}
                  percentage={40}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <ModalKYCIdentity
        onClose={() => {
          sessionStorage.removeItem("DocumentKsyc");
          setModalIdentity(false);
        }}
        open={modalIdentity}
      />
    </>
  );
};
